import { NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ActionButtonOutcomesType,
  BaseCssProperties,
  PaymentProvider,
  ProviderClass,
  TextItemWidgetConfig,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { AccountService } from '../../../../lib/services/account-service/account-service.service';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { AccountState } from '../../../../lib/state/account.state';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { handleRedirection } from '../../../../lib/utils/cta-button-actions/cta-redirection.utils';
import { loadAirwallexComponentsSdk } from '../../../../lib/utils/airwallex-components-sdk.util';

/** Statuses the Airwallex `kyc` element reports through its `ready` event. */
type AirwallexKycStatus = 'INIT' | 'SUBMITTED' | 'SUCCESS' | 'FAILURE';

/**
 * Maps a terminal KYC status onto the outcome the builder's "Configure Navigation" popup uses.
 * INIT/SUBMITTED are not terminal — like payment-cancellation-widget, only Success/Rejected are
 * configurable, so they're left out and simply never navigate.
 */
const KYC_STATUS_OUTCOMES: Partial<Record<AirwallexKycStatus, ActionButtonOutcomesType>> = {
  SUCCESS: ActionButtonOutcomesType.SUCCESS,
  FAILURE: ActionButtonOutcomesType.FAILURE,
};

@Component({
  selector: 'finxone-platform-airwallex-kyc-widget',
  templateUrl: './airwallex-kyc-widget.component.html',
  styleUrls: ['./airwallex-kyc-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [NgStyle],
})
export class AirwallexKycWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  // Not `static: true`: the host sits inside an `@if`, and static queries never see embedded views.
  @ViewChild('kycHost') kycHost?: ElementRef<HTMLDivElement>;

  attri: UiZoneWidgetAttributeConfig;
  contentStyle: baseCssConfigStyle;
  message = '';
  error = '';
  loading = true;
  hidden = false;
  status: AirwallexKycStatus = 'INIT';

  private element?: { unmount?: () => void };
  private shouldMount = false;
  private readonly injector = inject(Injector);

  constructor(
    private readonly configService: ConfigService,
    public dataIn: ProviderClass<TextItemWidgetConfig>,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    this.attri = this.dataIn?.attri;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
    ]);
    this.message =
      this.attri?.widgetProperties?.textContent || 'Complete your verification to activate your account.';

    // The builder canvas must not mint a real auth code every time it renders the widget.
    if (this.configService.getActiveConfigType().isWorkflow) {
      this.loading = false;
      return;
    }

    const provider = this.store.selectSnapshot(
      (state: { projectSettings?: { provider?: string } }) => state.projectSettings?.provider,
    );
    if (provider !== PaymentProvider.AIRWALLEX) {
      this.hidden = true;
      this.loading = false;
      return;
    }

    this.shouldMount = true;
  }

  // Mounting waits for the view: the host div only exists once `@if (!hidden)` has rendered.
  async ngAfterViewInit() {
    if (!this.shouldMount) {
      return;
    }
    try {
      await this.mountAirwallexKyc();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Unable to load the verification form.';
      this.loading = false;
    }
  }

  ngOnDestroy() {
    this.element?.unmount?.();
  }

  private async mountAirwallexKyc() {
    const profile = this.store.selectSnapshot(ProfileState.getProfile);
    const selected = this.store.selectSnapshot(AccountState.getCurrentAccount);
    const organisationId = profile?.activeOrganisationId;
    const userId = profile?.id;
    if (!organisationId || !userId) {
      throw new Error('Sign in to complete your verification.');
    }

    const authorize = await firstValueFrom(
      this.accountService.authorizeAirwallexRfi({
        organisationId,
        userId,
        accountId: selected?.accountId,
        connectedAccountId: selected?.attributes?.['connectedAccountId'],
        component: 'kyc',
      }),
    );

    const sdk = await loadAirwallexComponentsSdk();
    await sdk.init({
      elements: ['kyc'],
      langKey: 'en',
      // Must match the environment that minted the auth code, not a hardcoded guess.
      env: authorize.env ?? 'prod',
      clientId: authorize.clientId,
      authCode: authorize.authCode,
      codeVerifier: authorize.codeVerifier,
    });

    // hideNav is deliberately not set: the KYC flow is multi-step and the nav is how users move on.
    const element = await sdk.createElement('kyc', { hideHeader: true });
    const host = this.kycHost?.nativeElement;
    if (!host) {
      throw new Error('Verification container is missing.');
    }

    element.on?.('ready', (event?: unknown) => {
      this.applyStatus((event as { kycStatus?: AirwallexKycStatus })?.kycStatus ?? 'INIT');
    });
    element.on?.('success', () => {
      this.applyStatus('SUCCESS');
    });
    element.on?.('error', (event?: unknown) => {
      this.error = (event as { message?: string })?.message || 'Verification could not be completed.';
      this.applyStatus('FAILURE');
    });

    element.mount(host);
    this.element = element;
  }

  /**
   * Updates the status shown in the widget and, only when the app builder has configured
   * navigation for this outcome via "Configure Navigation", performs it — otherwise the widget
   * stays put and shows its own status copy, since navigation is opt-in per outcome.
   */
  private applyStatus(status: AirwallexKycStatus) {
    this.status = status;
    this.loading = false;

    const outcome = KYC_STATUS_OUTCOMES[status];
    if (outcome && this.attri?.widgetProperties?.actionNavigationConfigurations?.[outcome]) {
      handleRedirection(this.attri.widgetProperties, this.injector, outcome);
    }
  }
}
